import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Logs Page
 *
 * Displays chat log status, download controls, and archived log files.
 * Fetches data from the WP REST API /woo-ai/v1/logs/ endpoints.
 *
 * @package WooAIChatbot
 */
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'motion/react';
/* ------------------------------------------------------------------ */
/*  Styles                                                             */
/* ------------------------------------------------------------------ */
const styles = {
    page: {
        maxWidth: '800px',
        margin: '0 auto',
        padding: '32px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
        lineHeight: '1.5',
    },
    header: {
        marginBottom: '32px',
    },
    title: {
        fontSize: '24px',
        fontWeight: 700,
        color: '#111827',
        marginBottom: '8px',
    },
    subtitle: {
        fontSize: '14px',
        color: '#6b7280',
        marginTop: '8px',
    },
    card: {
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '16px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        overflow: 'hidden',
        marginBottom: '24px',
    },
    cardHeader: {
        padding: '20px 24px',
        borderBottom: '1px solid #f3f4f6',
    },
    cardTitle: {
        fontSize: '18px',
        fontWeight: 600,
        color: '#111827',
        margin: 0,
    },
    cardBody: {
        padding: '24px',
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '20px',
        marginBottom: '24px',
    },
    statBox: {
        padding: '16px',
        backgroundColor: '#f9fafb',
        borderRadius: '12px',
        textAlign: 'center',
    },
    statValue: {
        fontSize: '28px',
        fontWeight: 700,
        color: '#111827',
        marginBottom: '4px',
    },
    statLabel: {
        fontSize: '13px',
        color: '#6b7280',
    },
    progressBarOuter: {
        width: '100%',
        height: '8px',
        backgroundColor: '#e5e7eb',
        borderRadius: '4px',
        overflow: 'hidden',
        marginBottom: '8px',
    },
    progressBarInner: (pct) => ({
        width: `${Math.min(pct, 100)}%`,
        height: '100%',
        backgroundColor: pct > 80 ? '#ef4444' : pct > 50 ? '#f59e0b' : '#22c55e',
        borderRadius: '4px',
        transition: 'width 0.4s ease',
    }),
    progressLabel: {
        fontSize: '13px',
        color: '#6b7280',
        textAlign: 'right',
        marginBottom: '20px',
    },
    buttonPrimary: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 20px',
        fontSize: '14px',
        fontWeight: 500,
        color: '#ffffff',
        backgroundColor: '#6366f1',
        border: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
    },
    buttonDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
    },
    archiveRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 0',
        borderBottom: '1px solid #f3f4f6',
    },
    archiveRowLast: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 0',
        borderBottom: 'none',
    },
    archiveName: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#111827',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    },
    archiveMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        fontSize: '13px',
        color: '#6b7280',
    },
    emptyState: {
        textAlign: 'center',
        padding: '32px 16px',
        color: '#9ca3af',
        fontSize: '14px',
    },
    errorBox: {
        padding: '16px',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '12px',
        color: '#dc2626',
        marginBottom: '24px',
        fontSize: '14px',
    },
    loadingContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        color: '#9ca3af',
        fontSize: '15px',
        gap: '12px',
    },
    refreshButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        fontSize: '13px',
        fontWeight: 500,
        color: '#6b7280',
        backgroundColor: 'transparent',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
    },
};
const spinnerKeyframes = `
  @keyframes wooai-log-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
function getRestConfig() {
    const globals = window.wooAiChatbot;
    // PHP localizes as `apiUrl` (already includes /woo-ai/v1/).
    // Fall back to constructing from restUrl or the wp-json default.
    const apiUrl = globals?.apiUrl;
    const restUrl = globals?.restUrl;
    const base = apiUrl
        ? apiUrl.replace(/\/$/, '') // use apiUrl directly (already has woo-ai/v1)
        : `${restUrl || '/wp-json'}/woo-ai/v1`;
    return {
        restUrl: base,
        nonce: globals?.nonce || '',
    };
}
async function apiFetch(endpoint) {
    const { restUrl, nonce } = getRestConfig();
    const url = restUrl.replace(/\/$/, '') + '/' + endpoint.replace(/^\//, '');
    const res = await fetch(url, {
        headers: {
            'X-WP-Nonce': nonce,
        },
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || body?.error || `Request failed (${res.status})`);
    }
    return res.json();
}
async function downloadBlob(endpoint, fallbackName) {
    const { restUrl, nonce } = getRestConfig();
    const url = restUrl.replace(/\/$/, '') + '/' + endpoint.replace(/^\//, '');
    const res = await fetch(url, {
        headers: {
            'X-WP-Nonce': nonce,
        },
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || body?.error || `Download failed (${res.status})`);
    }
    const disposition = res.headers.get('content-disposition') || '';
    const match = disposition.match(/filename="?([^";\s]+)"?/);
    const filename = match?.[1] || fallbackName;
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
}
function formatDate(dateStr) {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}
/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export const Logs = () => {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const fetchStatus = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await apiFetch('logs/status');
            setStatus(data);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load log status');
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);
    const handleDownload = async () => {
        try {
            setDownloading(true);
            setError(null);
            await downloadBlob('logs/download', 'wooai-chat-logs.zip');
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Download failed');
        }
        finally {
            setDownloading(false);
        }
    };
    /* ----- Loading state ----- */
    if (loading && !status) {
        return (_jsxs(_Fragment, { children: [_jsx("style", { children: spinnerKeyframes }), _jsxs("div", { style: styles.loadingContainer, children: [_jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", style: { animation: 'wooai-log-spin 1s linear infinite' }, children: _jsx("path", { d: "M21 12a9 9 0 1 1-6.219-8.56" }) }), "Loading log status..."] })] }));
    }
    const usagePct = status ? (status.chats_size_mb / status.max_size_mb) * 100 : 0;
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: spinnerKeyframes }), _jsxs(motion.div, { style: styles.page, initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.25, ease: 'easeOut' }, children: [_jsxs("div", { style: styles.header, children: [_jsx("h1", { style: styles.title, children: "Chat Logs" }), _jsx("p", { style: styles.subtitle, children: "View log status, download current chat logs, and manage archived files." })] }), error && _jsx("div", { style: styles.errorBox, children: error }), status && (_jsxs(_Fragment, { children: [_jsxs(motion.div, { style: styles.card, initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.2, delay: 0.05 }, children: [_jsxs("div", { style: { ...styles.cardHeader, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }, children: [_jsx("h2", { style: styles.cardTitle, children: "Log Status" }), _jsxs("button", { style: styles.refreshButton, onClick: fetchStatus, disabled: loading, title: "Refresh status", children: [_jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", style: loading ? { animation: 'wooai-log-spin 1s linear infinite' } : undefined, children: [_jsx("path", { d: "M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" }), _jsx("path", { d: "M3 3v5h5" }), _jsx("path", { d: "M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" }), _jsx("path", { d: "M16 16h5v5" })] }), "Refresh"] })] }), _jsxs("div", { style: styles.cardBody, children: [_jsxs("div", { style: styles.statsGrid, children: [_jsxs("div", { style: styles.statBox, children: [_jsx("div", { style: styles.statValue, children: status.chats_count }), _jsx("div", { style: styles.statLabel, children: "Chat Files" })] }), _jsxs("div", { style: styles.statBox, children: [_jsx("div", { style: styles.statValue, children: status.chats_size_mb }), _jsx("div", { style: styles.statLabel, children: "Size (MB)" })] }), _jsxs("div", { style: styles.statBox, children: [_jsx("div", { style: styles.statValue, children: status.max_size_mb }), _jsx("div", { style: styles.statLabel, children: "Max Size (MB)" })] })] }), _jsx("div", { style: styles.progressBarOuter, children: _jsx("div", { style: styles.progressBarInner(usagePct) }) }), _jsxs("div", { style: styles.progressLabel, children: [status.chats_size_mb, " / ", status.max_size_mb, " MB used (", usagePct.toFixed(1), "%)"] }), _jsxs("button", { style: {
                                                    ...styles.buttonPrimary,
                                                    ...(downloading || status.chats_count === 0 ? styles.buttonDisabled : {}),
                                                }, onClick: handleDownload, disabled: downloading || status.chats_count === 0, children: [downloading ? (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", style: { animation: 'wooai-log-spin 1s linear infinite' }, children: _jsx("path", { d: "M21 12a9 9 0 1 1-6.219-8.56" }) })) : (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("path", { d: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" }), _jsx("polyline", { points: "7 10 12 15 17 10" }), _jsx("line", { x1: "12", y1: "15", x2: "12", y2: "3" })] })), downloading ? 'Downloading...' : 'Download Current Logs'] })] })] }), _jsxs(motion.div, { style: styles.card, initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.2, delay: 0.1 }, children: [_jsx("div", { style: styles.cardHeader, children: _jsxs("h2", { style: styles.cardTitle, children: ["Archives", ' ', status.archives.length > 0 && (_jsxs("span", { style: { fontSize: '14px', fontWeight: 400, color: '#9ca3af' }, children: ["(", status.archives.length, ")"] }))] }) }), _jsx("div", { style: styles.cardBody, children: status.archives.length === 0 ? (_jsx("div", { style: styles.emptyState, children: "No archived logs yet. Archives are created automatically when logs exceed the size limit." })) : (status.archives.map((archive, idx) => (_jsxs("div", { style: idx === status.archives.length - 1
                                                ? styles.archiveRowLast
                                                : styles.archiveRow, children: [_jsxs("div", { children: [_jsx("div", { style: styles.archiveName, children: archive.name }), _jsx("div", { style: { fontSize: '12px', color: '#9ca3af', marginTop: '2px' }, children: formatDate(archive.date) })] }), _jsx("div", { style: styles.archiveMeta, children: _jsxs("span", { children: [archive.size_mb, " MB"] }) })] }, archive.name)))) })] })] }))] })] }));
};
export default Logs;
